import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import Polyglot from 'node-polyglot';
import { useNavigate, useLocation } from 'hooks/use-router/use-router';
import { Overlay } from './locale.styles';
import {
  SupportedLocalesValues,
  TParams,
  LocaleContextInterface,
  LocaleProviderInterface,
} from './locale.types';
import { defaultLocale, languageKeysRoot, getIsRTL } from './locale.constants';
import { Spinner, SPINNER_SIZES } from 'components/elements/spinner/Spinner';
import { RouteLoading } from 'components/elements/route-loading/RouteLoading';
import { getLocaleFromParam, setCSSDirection } from './locale.helpers';
import { useSessionContext } from 'utils/context/session-provider';
import { AppLogger, Localisation } from '@workivate/tho-web-shared';
import { buildUrlWithParams } from 'utils/helpers';

export const LocaleContext = createContext<LocaleContextInterface>({
  shouldPersistLoginPageLanguage: false,
  setShouldPersistLoginPageLanguage: () => {},
  locale: defaultLocale,
  helpLinks: Localisation.getAllHelpLinks({
    locale: defaultLocale,
    environment: 'live',
  }),
  isRTL: getIsRTL(defaultLocale),
  t: (key: string) => key,
  setLocale: () => Promise.resolve(),
  isFetchingTranslations: false,
  genericErrorText: undefined,
});

const LocaleProvider = ({
  children,
}: LocaleProviderInterface): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasInitialized = useRef(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [isFetchingTranslations, setIsFetchingTranslations] =
    useState<boolean>(true);
  const [shouldPersistLoginPageLanguage, setShouldPersistLoginPageLanguage] =
    useState(false);
  const [genericErrorText, setGenericErrorText] = useState<
    string | undefined
  >();
  const [currentLocale, setCurrentLocale] =
    useState<SupportedLocalesValues>(defaultLocale);

  const [isRTL, setIsRTL] = useState<boolean>(
    getIsRTL(currentLocale || defaultLocale)
  );

  const [helpLinks, setHelpLinks] = useState(
    Localisation.getAllHelpLinks({
      locale: currentLocale || defaultLocale,
      environment: 'live',
    })
  );

  const { showGenericError } = useSessionContext();

  const polyglot = useRef<Polyglot>(new Polyglot());

  const loadPhrases = async (locale: SupportedLocalesValues): Promise<void> => {
    try {
      setIsFetchingTranslations(true);

      const response = await fetch(
        `${
          import.meta.env.REACT_APP_TRANSLATIONS_URL
        }/${locale}-lw-identity.json`
      );

      const phrases = await response.json();

      polyglot.current.extend(phrases);
    } catch (err) {
      AppLogger.logWithOptions({
        error: err,
        message: `Error loading translation phrases for locale "${locale}"`,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
    } finally {
      setIsFetchingTranslations(false);
      setIsFirstLoad(false);
      setGenericErrorText(
        polyglot.current.t('lw_unified_identity_app.generic_error')
      );
    }
  };

  const setLocale = useCallback(
    async (locale: SupportedLocalesValues) => {
      let localeToUse = locale;

      try {
        await loadPhrases(locale);
      } catch {
        await loadPhrases(defaultLocale);
        localeToUse = defaultLocale;
      }

      setCurrentLocale(localeToUse);
      setIsRTL(getIsRTL(localeToUse));

      // update the browser URL with the new locale
      const currentParams = new URLSearchParams(location.search);
      const currentLang = currentParams.get('lang');

      if (currentLang !== localeToUse) {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('lang', localeToUse);

        // Use buildUrlWithParams for iOS-safe encoding (%20 instead of +)
        const newUrl = buildUrlWithParams(location.pathname, newSearchParams);
        navigate(newUrl);
      }
    },
    [navigate, location.pathname, location.search]
  );

  useEffect(() => {
    // Only initialize once to avoid re-running when URL changes
    if (hasInitialized.current) return;

    // Initial locale follows the hierarchy -
    // 1. lang query param
    // 2. en_GB
    // Note: we do not currently set or read lang in local storage for USL
    // We rely on the originating app to set lang in the URL
    const initialiseLocale = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const langParam = params.get('lang');
        const redirectURIParam = params.get('redirect_uri');

        const localeFromParam = getLocaleFromParam(langParam, redirectURIParam);

        if (localeFromParam) {
          setShouldPersistLoginPageLanguage(true); // originating app has sent lang so we persist it
        }
        await setLocale(localeFromParam || defaultLocale);
        hasInitialized.current = true;
      } catch (err) {
        AppLogger.logWithOptions({
          error: err,
          message: 'Error initializing locale',
          shouldLogToDatadog: true,
          shouldLogToDynatrace: true,
        });
      }
    };
    initialiseLocale();
  }, [setLocale]);

  useEffect(() => {
    setCSSDirection(isRTL);
  }, [isRTL]);

  useEffect(() => {
    setHelpLinks(
      Localisation.getAllHelpLinks({
        locale: currentLocale || defaultLocale,
        environment: 'live',
      })
    );
  }, [currentLocale]);

  const t = (key: string, params?: TParams) => polyglot.current.t(key, params);

  const contextValue = useMemo(
    () => ({
      shouldPersistLoginPageLanguage,
      setShouldPersistLoginPageLanguage,
      setLocale,
      locale: currentLocale ?? defaultLocale,
      helpLinks,
      isRTL,
      t,
      isFetchingTranslations,
      genericErrorText: showGenericError ? genericErrorText : undefined,
    }),
    [
      currentLocale,
      showGenericError,
      genericErrorText,
      helpLinks,
      isRTL,
      isFetchingTranslations,
      setLocale,
      shouldPersistLoginPageLanguage,
      setShouldPersistLoginPageLanguage,
    ]
  );

  // if this is the first language load, there are no strings so we show only the spinner
  // to avoid broken polyglot strings
  // on subsequent loads, we can overlay a spinner
  // the strings will update when the new language is loaded
  // this avoids resetting the USL app when language changes
  if (isFetchingTranslations && isFirstLoad) return <RouteLoading />;

  return (
    <LocaleContext.Provider value={contextValue}>
      {isFetchingTranslations ? (
        <Overlay>
          <Spinner size={SPINNER_SIZES.page} />
        </Overlay>
      ) : null}
      {children}
    </LocaleContext.Provider>
  );
};

// When using the t() function, never dynamically generate the key.
// USE THE FULL KEY, e.g. 'forgotten_password.title' (don't include the prefix)
// otherwise it cannot be found in code, e.g. by the detect-unused-translations script
const usePolyglotT = (): LocaleContextInterface => {
  const { t, ...rest } = useContext(LocaleContext);

  return {
    t: (key: string, params?: TParams) =>
      t(`${languageKeysRoot}.${key}`, params),
    ...rest,
  };
};

export { usePolyglotT, LocaleProvider };
